import type { GeneratorOptions, Schema } from '@cli-types/core-engine/schema.js'
import type { DatabaseGeneratorResult } from '@cli-types/next-adapter/generators.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { toCamelCase } from '@cli-utils/text.js'
import { getManyToManyFields } from '@core-engine/schema/field-helpers/flattening/get-many-to-many-fields.js'
import { resolveExtensionNamespace } from '@next-adapter/config/paths/resolve-extension-namespace.js'
import { renderDatabaseImportsTemplate } from '@next-adapter/templates/renderers/generators/database/generate-database.js'
import { generateJunctionTable } from './generate-junction-table.js'
import { generateSelectOptionsTable } from './generate-select-options-table.js'
import { generateTableDefinition } from './generate-table-definition.js'
import { shouldEmitJunction } from './should-emit-junction.js'

export function generateDatabase(
  schema: Schema,
  dbDir: string,
  options: GeneratorOptions = {},
  relatedSchemas: Map<string, Schema> = new Map()
): DatabaseGeneratorResult {
  const outputNamespace = resolveExtensionNamespace(schema.name, options.outputNamespace)
  const requiredImports = new Set<string>()
  const siblingImports = new Set<string>()
  const needsSql = { value: false }

  const tableDef = generateTableDefinition(schema, requiredImports, needsSql)
  const selectOptionsTableDef = generateSelectOptionsTable(schema, requiredImports, needsSql)

  const emittedJunctions = getManyToManyFields(schema.fields)
    .filter((field) => shouldEmitJunction(schema.name, field, relatedSchemas))
    .map((field) =>
      generateJunctionTable(schema.name, field, requiredImports, siblingImports, outputNamespace)
    )

  const importBlocks: string[] = []
  if (needsSql.value) {
    importBlocks.push(`import { sql } from 'drizzle-orm'`)
  }

  const pgCoreImports = Array.from(requiredImports).sort()
  if (pgCoreImports.length > 0) {
    importBlocks.push(renderDatabaseImportsTemplate({ join: pgCoreImports.join(',\n  ') }))
  }

  if (siblingImports.size > 0) {
    importBlocks.push(...Array.from(siblingImports).sort())
  }

  importBlocks.push(
    outputNamespace === 'core'
      ? `import { user } from './schema'`
      : `import { user } from '../core/schema'`
  )

  const sections = [
    importBlocks.join('\n'),
    tableDef.trim(),
    selectOptionsTableDef?.trim(),
    ...emittedJunctions.map((junction) => junction.content.trim())
  ].filter(Boolean)

  const content = `${sections.join('\n\n')}\n`
  const tableName = toCamelCase(schema.name)

  return {
    files: [createGeneratedFile(`${dbDir}/${outputNamespace}/${schema.name}.ts`, content)],
    tableName,
    junctionTables: emittedJunctions.map((junction) => junction.name)
  }
}
